import { useEffect } from 'react';
import { shouldPopUp, shownKey } from '../model/myUnread';
import type { LatestNotice } from '../types';

const supported = () => typeof window !== 'undefined' && 'Notification' in window;

const readShown = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeShown = (key: string, id: string) => {
  try {
    window.localStorage.setItem(key, id);
  } catch {
    // private mode: the notice may show again after a reload, which is harmless
  }
};

/**
 * Shows the server's latest unread-mail notice as a desktop pop-up, once per notice, so the web and the phone
 * show the same notice at the same time (the rule lives on the server). Only when the person has allowed
 * notifications; asking is done by a button (`askNotificationPermission`), never on page load.
 */
export function useUnreadNoticePopup(notice: LatestNotice | null | undefined, userName: string | null | undefined, onClick?: () => void) {
  const id = notice?.id ?? null;
  useEffect(() => {
    if (!notice || !supported() || !userName) return;
    const key = shownKey(userName);
    if (!shouldPopUp(notice, readShown(key), Date.now())) return;
    writeShown(key, notice.id);
    if (Notification.permission !== 'granted') return;
    try {
      const popup = new Notification(notice.title, { body: notice.body, tag: 'mail-unread', icon: '/images/notify-icon.png' });
      popup.onclick = () => {
        window.focus();
        popup.close();
        onClick?.();
      };
    } catch {
      // some browsers only allow notifications from a service worker; the badge still shows the count
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per notice id
  }, [id, userName]);
}

export type NotificationState = 'granted' | 'denied' | 'default' | 'unsupported';

export const notificationState = (): NotificationState => (supported() ? Notification.permission : 'unsupported');

export const askNotificationPermission = async (): Promise<NotificationState> =>
  supported() ? Notification.requestPermission() : 'unsupported';
